import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useApi } from '../../hooks/useApi';
import { register, getSchools } from '../../services/api';
import ErrorCard from '../../components/ErrorCard/ErrorCard';
import DarkModeToggle from '../../components/DarkModeToggle/DarkModeToggle';
import './Register.css';

const Register = () => {
  const [formData, setFormData] = useState({ 
    email: '', 
    password: '', 
    name: '',
    confirmPassword: '',
    schoolId: ''
  });
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [schools, setSchools] = useState([]);
  const [schoolSearch, setSchoolSearch] = useState('');
  const [showSchoolDropdown, setShowSchoolDropdown] = useState(false);
  const [loadingSchools, setLoadingSchools] = useState(false);
  const schoolDropdownRef = useRef(null);
  const { handleRequest, loading } = useApi();
  const navigate = useNavigate();

  // Fetch schools when search changes
  useEffect(() => {
    const fetchSchools = async () => {
      if (schoolSearch.length < 2) {
        setSchools([]);
        return;
      }
      setLoadingSchools(true);
      try {
        const response = await getSchools(schoolSearch);
        // Handle both nested and flat response structures
        const schoolsData = response.data?.data || response.data || [];
        setSchools(Array.isArray(schoolsData) ? schoolsData : []);
      } catch (error) {
        console.error('Failed to fetch schools:', error);
        setSchools([]);
      } finally {
        setLoadingSchools(false);
      }
    };

    const timeoutId = setTimeout(fetchSchools, 300);
    return () => clearTimeout(timeoutId);
  }, [schoolSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (schoolDropdownRef.current && !schoolDropdownRef.current.contains(event.target)) {
        setShowSchoolDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrorMessage('');
  };

  const handleSchoolSearchChange = (e) => {
    const value = e.target.value;
    setSchoolSearch(value);
    setShowSchoolDropdown(true);
    if (!value) {
      setFormData(prev => ({ ...prev, schoolId: '' }));
    }
  };

  const handleSchoolSelect = (school) => {
    setFormData(prev => ({ ...prev, schoolId: school._id || school.id }));
    setSchoolSearch(school.name);
    setShowSchoolDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      setErrorMessage('Passordene matcher ikke');
      return;
    }
    try {
      await handleRequest(register, {
        email: formData.email,
        password: formData.password,
        name: formData.name,
        schoolId: formData.schoolId || undefined
      });
      setSuccessMessage('Registrering fullført! Kontoen din venter på godkjenning fra administrator. Du kan logge inn når den er godkjent.');
      setFormData({ email: '', password: '', name: '', confirmPassword: '' });
    } catch (error) {
      setErrorMessage(error.message || 'Registrering mislyktes. Prøv igjen.');
    }
  };

  return (
    <div className="register-container">
      <div className="dark-mode-toggle-wrapper">
        <DarkModeToggle />
      </div>
      <div className="register-card">
        <h2>Lærerregistrering</h2>
        {errorMessage && (
          <ErrorCard 
            message={errorMessage} 
            onClose={() => setErrorMessage('')}
          />
        )}
        {successMessage && (
          <div className="success-message">
            {successMessage}
          </div>
        )}
        <p className="register-description">
          Registrer deg som lærer. Kontoen din må godkjennes av administrator før du kan logge inn.
        </p>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Fullt navn"
              required
            />
          </div>
          <div className="form-group">
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="E-post"
              required
            />
          </div>
          <div className="form-group">
            <input
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Passord"
              required
            />
          </div>
          <div className="form-group">
            <input
              type="password"
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Bekreft passord"
              required
            />
          </div>
          <div className="form-group">
            <div className="school-select-wrapper" ref={schoolDropdownRef}>
              <input
                type="text"
                value={schoolSearch}
                onChange={handleSchoolSearchChange}
                onFocus={() => setShowSchoolDropdown(true)}
                placeholder="Søk etter skolen din (valgfritt)"
                className="school-search-input"
              />
              {showSchoolDropdown && schoolSearch.length >= 2 && (
                <div className="school-dropdown">
                  {loadingSchools ? (
                    <div className="school-dropdown-item">Laster...</div>
                  ) : schools.length === 0 ? (
                    <div className="school-dropdown-item">Ingen skoler funnet</div>
                  ) : (
                    schools.map((school) => (
                      <div
                        key={school._id || school.id}
                        className="school-dropdown-item"
                        onClick={() => handleSchoolSelect(school)}
                      >
                        <div className="school-name">{school.name}</div>
                        {(school.city || school.country) && (
                          <div className="school-location">
                            {[school.city, school.country].filter(Boolean).join(', ')}
                          </div>
                        )}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>
          <button type="submit" disabled={loading || !!successMessage}>
            {loading ? 'Laster...' : 'Registrer'}
          </button>
        </form>
        <p className="auth-link">
          Har du allerede en konto? <Link to="/login">Logg inn</Link>
        </p>
        <p className="auth-link">
          Er du elev? <Link to="/join">Bli med i en klasse her</Link>
        </p>
      </div>
    </div>
  );
};

export default Register; 